import { useState } from 'react';
import { Shield, ArrowLeft } from 'lucide-react';

/**
 * MFA verification step shown during login when user has TOTP enabled.
 * Props:
 *   - pendingUserId: the user ID from the initial login response
 *   - onSuccess: callback with { success, user, mustChangePassword }
 *   - onBack: callback to go back to login form
 */
export default function MfaChallenge({ pendingUserId, onSuccess, onBack }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (code.length < 6) {
      setError('Enter a 6-digit code from your authenticator app, or an 8-character backup code.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/auth/mfa-verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId: pendingUserId, token: code }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        onSuccess(data);
      } else {
        setError(data.error || 'Verification failed. Please try again.');
      }
    } catch (err) {
      setError('Verification failed. Please check your connection.');
    }
    setLoading(false);
  };

  return (
    <div className="mfa-challenge">
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(0,212,170,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
          <Shield size={28} style={{ color: '#00d4aa' }} />
        </div>
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 4px' }}>Two-Factor Authentication</h2>
        <p style={{ color: 'var(--text-tertiary)', fontSize: 13, margin: 0 }}>
          Enter the verification code from your authenticator app
        </p>
      </div>

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: '#ef4444', fontSize: 13, textAlign: 'center' }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: 16 }}>
          <input
            type="text"
            inputMode="numeric"
            maxLength={8}
            autoComplete="one-time-code"
            autoFocus
            value={code}
            onChange={e => setCode(e.target.value.replace(/[^a-fA-F0-9]/g, ''))}
            placeholder="000000"
            className="login-input"
            style={{ textAlign: 'center', letterSpacing: 6, fontSize: 20, fontFamily: 'monospace' }}
          />
        </div>
        <button type="submit" disabled={loading || code.length < 6} className="login-btn" style={{ width: '100%' }}>
          {loading ? 'Verifying...' : 'Verify'}
        </button>
      </form>

      <div style={{ textAlign: 'center', marginTop: 16 }}>
        <p style={{ fontSize: 12, color: 'var(--text-tertiary)', margin: '0 0 8px' }}>
          Lost your authenticator? Enter an 8-character backup code instead.
        </p>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <ArrowLeft size={14} /> Back to login
        </button>
      </div>
    </div>
  );
}
